import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import NotFound from "@/pages/NotFound";
import { pillarArticles } from "@/mocks/articles";
import { MAIN_SITE_URL } from "@/mocks/categories";
import JsonLd from "@/components/feature/JsonLd";
import { SITE_URL, SITE_NAME, AUTHOR_NAME, DATE_PUBLISHED } from "@/lib/seo";

export default function Article() {
  const { slug } = useParams();
  const { t, i18n } = useTranslation();
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  const article = pillarArticles.find((a) => a.slug === slug);

  useEffect(() => {
    document.documentElement.style.scrollBehavior = "auto";
    window.scrollTo(0, 0);
    document.documentElement.style.scrollBehavior = "";
  }, [slug]);

  if (!article) {
    return <NotFound />;
  }

  const p = `pillar.${article.slug}`;

  const sections = [
    { id: "s1", title: `${p}.s1Title`, paras: [`${p}.s1p1`, `${p}.s1p2`] },
    { id: "s2", title: `${p}.s2Title`, paras: [`${p}.s2p1`, `${p}.s2p2`] },
    { id: "s3", title: `${p}.s3Title`, paras: [`${p}.s3p1`, `${p}.s3p2`] },
  ];

  const checklist = [1, 2, 3, 4];
  const faqItems = [
    { q: `${p}.faq1q`, a: `${p}.faq1a` },
    { q: `${p}.faq2q`, a: `${p}.faq2a` },
  ];

  const related = pillarArticles.filter((a) => a.slug !== slug).slice(0, 3);

  const articleUrl = `${SITE_URL}/${article.slug}`;
  const headline = t(`${p}.h1`);
  const description = t(`${p}.meta.description`);

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline,
    description,
    url: articleUrl,
    mainEntityOfPage: articleUrl,
    author: { "@type": "Organization", name: AUTHOR_NAME },
    publisher: { "@type": "Organization", name: SITE_NAME, url: `${SITE_URL}/` },
    datePublished: DATE_PUBLISHED,
    dateModified: DATE_PUBLISHED,
    inLanguage: i18n.language,
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: t("nav.home"), item: `${SITE_URL}/` },
      { "@type": "ListItem", position: 2, name: headline, item: articleUrl },
    ],
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqItems.map((item) => ({
      "@type": "Question",
      name: t(item.q),
      acceptedAnswer: { "@type": "Answer", text: t(item.a) },
    })),
  };

  return (
    <div id="top" className="min-h-screen bg-background-50">
      <JsonLd data={articleSchema} />
      <JsonLd data={breadcrumbSchema} />
      <JsonLd data={faqSchema} />
      <Navbar />
      <main className="pt-16 md:pt-20">
        {/* Article header */}
        <header className="px-4 md:px-6 py-12 md:py-16 bg-primary-50 border-b border-background-200/70">
          <div className="mx-auto w-full max-w-3xl">
            <nav className="text-sm text-foreground-500" aria-label="Breadcrumb">
              <Link to="/" className="hover:text-primary-700 cursor-pointer transition-colors">
                {t("nav.home")}
              </Link>
              <span className="mx-2 text-foreground-400">/</span>
              <span className="text-foreground-700">{t(`${p}.cardTitle`)}</span>
            </nav>

            <span className="mt-5 inline-flex items-center gap-2 rounded-full bg-primary-100 px-3 py-1 text-xs md:text-sm font-medium text-primary-800">
              <i className="ri-book-open-line"></i>
              {t(`${p}.badge`)}
            </span>

            <h1 className="mt-4 text-3xl md:text-5xl font-heading font-semibold text-foreground-950 leading-tight">
              {t(`${p}.h1`)}
            </h1>

            <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-foreground-500">
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-user-line"></i>
                {t("articleMeta.authorLabel")}: {t("articleMeta.author")}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-calendar-line"></i>
                {t("articleMeta.publishedLabel")}: {t("articleMeta.published")}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-refresh-line"></i>
                {t("articleMeta.updatedLabel")}: {t("articleMeta.updated")}
              </span>
            </div>
          </div>
        </header>

        <div className="px-4 md:px-6 py-12 md:py-16">
          <article className="mx-auto w-full max-w-3xl">
            {/* Short answer */}
            <div className="rounded-xl border-l-4 border-primary-500 bg-primary-100/60 p-5 md:p-6">
              <p className="text-xs font-semibold uppercase tracking-wide text-primary-800">
                {t("articleMeta.shortAnswerLabel")}
              </p>
              <p className="mt-2 text-base md:text-lg text-foreground-800 leading-relaxed">
                {t(`${p}.shortAnswer`)}
              </p>
            </div>

            <p className="mt-8 text-base md:text-lg text-foreground-700 leading-relaxed">
              {t(`${p}.intro1`)}
            </p>
            <p className="mt-4 text-base md:text-lg text-foreground-600 leading-relaxed">
              {t(`${p}.intro2`)}
            </p>

            <p className="mt-5">
              <Link
                to="/"
                className="inline-flex items-center gap-1.5 text-primary-700 font-medium cursor-pointer hover:text-primary-800 transition-colors"
              >
                {t("button.backHome")}
                <i className="ri-arrow-left-line"></i>
              </Link>
            </p>

            {/* TOC */}
            <nav className="mt-10 rounded-xl border border-background-200 bg-background-100 p-5 md:p-6">
              <h2 className="text-sm font-semibold text-foreground-900">{t("articleMeta.tocTitle")}</h2>
              <ul className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
                {sections.map((s) => (
                  <li key={s.id}>
                    <a
                      href={`#${s.id}`}
                      className="text-sm text-foreground-600 hover:text-primary-700 cursor-pointer transition-colors"
                    >
                      {t(s.title)}
                    </a>
                  </li>
                ))}
                <li>
                  <a
                    href="#checklist"
                    className="text-sm text-foreground-600 hover:text-primary-700 cursor-pointer transition-colors"
                  >
                    {t("articleMeta.checklistTitle")}
                  </a>
                </li>
                <li>
                  <a
                    href="#faq"
                    className="text-sm text-foreground-600 hover:text-primary-700 cursor-pointer transition-colors"
                  >
                    {t("articleMeta.faqTitle")}
                  </a>
                </li>
                <li>
                  <a
                    href="#conclusion"
                    className="text-sm text-foreground-600 hover:text-primary-700 cursor-pointer transition-colors"
                  >
                    {t("articleMeta.conclusionTitle")}
                  </a>
                </li>
              </ul>
            </nav>

            {/* Sections */}
            {sections.map((section) => (
              <section key={section.id} id={section.id} className="mt-12 scroll-mt-24">
                <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-950">
                  {t(section.title)}
                </h2>
                {section.paras.map((para) => (
                  <p key={para} className="mt-4 text-base md:text-lg text-foreground-700 leading-relaxed">
                    {t(para)}
                  </p>
                ))}
              </section>
            ))}

            {/* Checklist */}
            <section id="checklist" className="mt-12 scroll-mt-24">
              <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-950">
                {t("articleMeta.checklistTitle")}
              </h2>
              <ul className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
                {checklist.map((n) => (
                  <li
                    key={n}
                    className="flex items-start gap-3 rounded-lg border border-background-200 bg-background-50 px-4 py-3"
                  >
                    <span className="w-6 h-6 flex items-center justify-center rounded-full bg-primary-500 text-background-50 shrink-0 mt-0.5">
                      <i className="ri-check-line text-sm"></i>
                    </span>
                    <span className="text-sm md:text-base text-foreground-800">{t(`${p}.k${n}`)}</span>
                  </li>
                ))}
              </ul>
            </section>

            {/* FAQ */}
            <section id="faq" className="mt-12 scroll-mt-24">
              <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-950">
                {t("articleMeta.faqTitle")}
              </h2>
              <div className="mt-5 space-y-3">
                {faqItems.map((item, i) => (
                  <div key={item.q} className="rounded-lg border border-background-200 bg-background-50 overflow-hidden">
                    <button
                      type="button"
                      onClick={() => setOpenFaq(openFaq === i ? null : i)}
                      className="flex w-full items-center justify-between gap-3 px-4 md:px-5 py-4 text-left cursor-pointer hover:bg-background-100/60 transition-colors"
                    >
                      <span className="text-sm md:text-base font-semibold text-foreground-900">
                        {t(item.q)}
                      </span>
                      <span className="w-6 h-6 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 shrink-0">
                        <i className={`ri-${openFaq === i ? "subtract" : "add"}-line text-base`}></i>
                      </span>
                    </button>
                    {openFaq === i && (
                      <div className="px-4 md:px-5 pb-5">
                        <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                          {t(item.a)}
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </section>

            {/* Conclusion */}
            <section id="conclusion" className="mt-12 scroll-mt-24">
              <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-950">
                {t("articleMeta.conclusionTitle")}
              </h2>
              <p className="mt-4 text-base md:text-lg text-foreground-700 leading-relaxed">
                {t(`${p}.conclusion`)}
              </p>
            </section>

            {/* Related articles */}
            <div className="mt-12 rounded-xl border border-background-200 bg-background-100 p-6 md:p-7">
              <h3 className="text-base md:text-lg font-heading font-semibold text-foreground-950">
                {t("nav.readGuide")}
              </h3>
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
                {related.map((r) => (
                  <Link
                    key={r.slug}
                    to={`/${r.slug}`}
                    className="group flex flex-col rounded-lg border border-background-200 bg-background-50 p-4 cursor-pointer hover:border-primary-300 transition-colors"
                  >
                    <span className="text-xs font-semibold text-primary-700">{t(`pillar.${r.slug}.badge`)}</span>
                    <span className="mt-1.5 text-sm font-medium text-foreground-900 leading-snug">
                      {t(`pillar.${r.slug}.cardTitle`)}
                    </span>
                  </Link>
                ))}
              </div>

              <div className="mt-6 flex flex-col sm:flex-row gap-3">
                <Link
                  to="/"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors"
                >
                  {t("button.backHome")}
                  <i className="ri-arrow-left-line"></i>
                </Link>
                <a
                  href={MAIN_SITE_URL}
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-background-300 px-5 py-2.5 text-sm font-semibold text-foreground-800 whitespace-nowrap cursor-pointer hover:bg-background-200/60 transition-colors"
                >
                  {t("button.visitMain")}
                  <i className="ri-arrow-right-up-line"></i>
                </a>
              </div>
            </div>

            {/* Disclaimer */}
            <p className="mt-10 text-xs text-foreground-500 leading-relaxed border-t border-background-200 pt-6">
              {t("articleMeta.disclaimer")}
            </p>
          </article>
        </div>
      </main>
      <Footer />
    </div>
  );
}